import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
  ErrorMessage, Button, Checkbox, Icon, InputField, PasswordField, FieldGroup,
} from 'anf-core-react';
import isValidEmail from '../../../tools/validations/isValidEmail';
import Tmnt from '../../Tmnt/Tmnt';
import useLog from '../../useLog/useLog';
import { userLoginApi, userExistsApi } from '../../../gql/recognizeCustomerForm.gql';
import PageHeader from '../../ShoppingBag/PageHeader/PageHeader';
import trackAction from '../../../tools/analytics';
import $window from '../../../tools/window';
import $document from '../../../tools/document';

export default function RecognizeCustomerForm({
  unmount,
  tmntData,
  isEmailSaved,
}) {
  const [step, setStep] = useState(1); // Step state: 1 = email only, 2 = full form
  const [emailInvalidTextMsg, setEmailInvalidTextMsg] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [originalEmailValue, setOriginalEmailValue] = useState(''); // Preserve original email from step 1
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [formErrorText, setFormErrorText] = useState('');
  const [displayFormError, setDisplayFormError] = useState(false);
  const logger = useLog('checkout.recognizeCustomerForm');
  const [keepMeSignedIn, setKeepMeSigned] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  // Button Hooks
  const [submitButtonLabel, setSubmitButtonLabel] = useState('');
  const [submitButtonDisabledStyle, setSubmitButtonDisabledStyle] = useState(false);
  const [continueAsGuestDisabled, setContinueAsGuestDisabled] = useState(false);
  const [submitButtonLeftIcon, setSubmitButtonLeftIcon] = useState(undefined);

  const updateSubmitButtonState = (labelReference, disabledStyle = false, leftIcon = undefined) => {
    setSubmitButtonLabel(labelReference?.value);
    setSubmitButtonDisabledStyle(disabledStyle);
    setSubmitButtonLeftIcon(leftIcon);
  };

  const onFailedLogin = (loginRes) => {
    const { statusMessages } = loginRes;
    const { message = '' } = statusMessages?.[0] || {};
    const { key = '' } = statusMessages?.[0] || {};

    switch (key) {
      case '._ERR_USER_DOES_NOT_EXIST':
        setEmailInvalidTextMsg(message);
        setEmailInvalid(true);
        setPasswordInvalid(false);
        setDisplayFormError(true);
        setFormErrorText(tmntData?.errorSomethingWentWrong?.value);
        // trackInvalidEmail();
        break;
      case '._ERR_PASSWORD_EXPIRED':
      case '.2030': // Mismatch email/password
      case '.2490': // Account temporary lock
        setPasswordInvalid(true);
        setDisplayFormError(true);
        setFormErrorText(message);
        // trackInvalidSignIn();
        break;
      default:
        setPasswordInvalid(true);
        break;
    }
  };

  const [userLoginMutation] = useMutation(userLoginApi, {
    onCompleted: ({ login }) => {
      if (login?.success) {
        logger.debug('userLoginMutation RESULT', login);
        // user successfully logged in,
        // also dispatch an event for subscribers
        const event = new Event('RecognizeCustomer:UserLoggedIn');
        $window.dispatchEvent(event);
        updateSubmitButtonState(tmntData?.buttonSuccess, true, 'check');
        // we have to pass keep_me_logged_in_flag based on the keep me Signed in checkbox
        trackAction('analytics.login_success', { keep_me_logged_in_flag: !!keepMeSignedIn });
      } else {
        onFailedLogin(login);
        // set button to its default state again
        updateSubmitButtonState('submit');
      }
    },
  });

  const continueAsGuest = useCallback(() => {
    if (continueAsGuestDisabled) return;
    setContinueAsGuestDisabled(true);
    setPasswordInvalid(false);
    setDisplayFormError(false);
    setFormErrorText('');

    // Use the original email value from step 1, not the current (possibly cleared) value
    const emailToSave = originalEmailValue || emailValue;
    sessionStorage.setItem('guestCustomerMailId', emailToSave);

    const event = new Event('RecognizeCustomer:ContinueAsGuest');
    $window.dispatchEvent(event);
    unmount();
  }, [continueAsGuestDisabled, emailValue, originalEmailValue, unmount]);

  const [checkUserExists] = useLazyQuery(userExistsApi, {
    onCompleted: (data) => {
      if (data.userExists) {
        logger.debug('User exists, proceeding to step 2');
      } else {
        continueAsGuest();
      }
      logger.debug('checkUserExists RESULT', data.userExists);
      setStep(2);
      // Preserve the email value from step 1
      setOriginalEmailValue(emailValue);
      updateSubmitButtonState(tmntData?.signInButton);
    },
    onError: (error) => {
      logger.error('checkUserExists ERROR', error);
      continueAsGuest();
      updateSubmitButtonState(tmntData?.signInButton);
    },
  });

  const {
    checkoutButtonText,
    email,
    passwordLabel,
    buttonProcessing,
    formError,
    signInButton,
    keepMeSignedInCheckBox,
    keepMeSignedInUncheckMsg,
    forgotPassword,
    continueAsGuestButton,
    passwordShow,
    passwordHide,
    emailInvalidFormatText,
    emailInvalidText,
    passwordInvalidText,
    recognizeCustomerStepOneHeader,
    recognizeCustomerStepOneText,
    recognizeCustomerStepTwoText,
    recognizeCustomerStepTwoHeader,
    continueButton,
  } = tmntData;
  const validateEmail = () => {
    const isValid = isValidEmail(emailValue);
    setEmailInvalid(!isValid);
    return isValid;
  };

  const loginUser = () => {
    userLoginMutation({
      variables: {
        email: emailValue,
        password: passwordValue,
      },
    });
  };

  const formSubmission = (e) => {
    e.preventDefault();
    if (submitButtonDisabledStyle) return;
    setEmailInvalidTextMsg('');
    setPasswordInvalid(false);
    setDisplayFormError(false);

    const emailIsValid = validateEmail();

    // Step 1: Only validate email and check if user exists
    if (step === 1) {
      if (!emailIsValid) {
        setFormErrorText(formError?.value);
        updateSubmitButtonState(continueButton);
        return;
      }
      // Call doesUserExist query and move to step 2 in the onCompleted callback
      updateSubmitButtonState(buttonProcessing, true, 'spinner');
      checkUserExists({
        variables: { email: emailValue },
      });
      return;
    }

    // Step 2: Validate both email and password for login
    if (!passwordValue) {
      setPasswordInvalid(true);
    }

    if (!emailIsValid || !passwordValue) {
      setFormErrorText(formError?.value);
      updateSubmitButtonState(signInButton);
      return;
    }

    updateSubmitButtonState(buttonProcessing, true, 'spinner');
    loginUser();
  };

  const handleForgotPasswordClick = () => {
    // Dispatch the custom event to open the modal for forgot password
    $document.dispatchEvent(new CustomEvent('create-a-password-modal:open'));
  };

  useEffect(() => {
    if (isEmailSaved) {
      continueAsGuest();
    }
  }, [continueAsGuest, isEmailSaved]);

  const handleShowPassword = () => setShowPassword((prev) => !prev);

  // This will continue as Guest if we have any Express ApplePay error
  useEffect(() => {
    const handleErrorEvent = () => {
      continueAsGuest();
    };
    $window.addEventListener('mfe:submit:error', handleErrorEvent);
    return () => {
      $window.removeEventListener('mfe:submit:error', handleErrorEvent);
    };
  }, [continueAsGuest]);

  return (
    <div>
      <PageHeader classes="checkout-heading" tmntKey="CHK_CHECKOUT" value={(<Tmnt tmnt={checkoutButtonText} tmntKey="CHK_CHECKOUT" />)} />
      <div className="inner-wrapper">
        <div className="header">
          {step === 1 ? (
            <>
              <h2 className="h2">
                <Tmnt tmnt={recognizeCustomerStepOneHeader} isHtml />
              </h2>
              <Tmnt tmnt={recognizeCustomerStepOneText} isHtml />
            </>
          ) : (
            <>
              <h2 className="h2">
                <Tmnt tmnt={recognizeCustomerStepTwoHeader} isHtml />
              </h2>
              <Tmnt tmnt={recognizeCustomerStepTwoText} isHtml />
            </>
          )}
        </div>
        <form onSubmit={formSubmission} name="recognize-customer-form">
          <FieldGroup
            legend="email address, password"
          >
            <InputField
              id="recognizeCustomerForm-email"
              type="email"
              label={email?.label?.value}
              name="email"
              value={emailValue}
              onChange={(e) => {
                setEmailInvalid(false);
                setEmailValue(e.target.value);
              }}
              onBlur={(e) => { if (e.target.value) validateEmail(e); }}
              isInvalid={emailInvalid}
              required
              autoComplete=""
              data-testid="recognizeCustomerForm-email"
            >
              {emailInvalid && !emailInvalidTextMsg && (
                <ErrorMessage id="email-invalid-error">
                  <Tmnt tmnt={emailInvalidText} isHtml />
                  {' '}
                  <Tmnt tmnt={emailInvalidFormatText} isHtml />
                </ErrorMessage>
              )}

              {emailInvalid && emailInvalidTextMsg && (
              <ErrorMessage id="email-invalid-error">
                {emailInvalidTextMsg}
              </ErrorMessage>
              )}

            </InputField>

            {step === 2 && (
              <PasswordField>
                <InputField
                  label={passwordLabel?.label?.value}
                  id="recognizeCustomerForm_password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordValue || ''}
                  onChange={(e) => setPasswordValue(e.target.value)}
                  name="password"
                  ariaDescribedBy="password"
                  isInvalid={passwordInvalid}
                  autoComplete=""
                >
                  <Button onClick={handleShowPassword}>
                    <Icon
                      icon={showPassword ? 'eye-filled' : 'eye'}
                      labelText={showPassword ? passwordShow?.value || '' : passwordHide?.value || ''}
                    />
                  </Button>
                </InputField>

                {passwordInvalid && (
                  <ErrorMessage id="email-invalid-error">
                    <Tmnt tmnt={passwordInvalidText} isHtml />
                  </ErrorMessage>
                )}
              </PasswordField>
            )}
          </FieldGroup>

          {step === 2 && (
            <div className="keep-me-signed-in-checkbox-container">
              <Checkbox
                description={(
                  <div className="signedIn-checkbox-content">
                    <span className="h4">
                      <Tmnt tmnt={keepMeSignedInCheckBox} isHtml />
                    </span>
                    <br />
                    <Tmnt tmnt={keepMeSignedInUncheckMsg} isHtml />
                  </div>
                )}
                id="keepMeSignedIn"
                name="keepMeSignedIn"
                onChange={() => { setKeepMeSigned(!keepMeSignedIn); }}
                isChecked={keepMeSignedIn}
                data-testid="keepMeSignedIn"
              />
            </div>
          )}

          <div hidden={!displayFormError}>
            <ErrorMessage id="recognizeCustomerForm__errorMessage">
              {formErrorText}
            </ErrorMessage>
          </div>

          {step === 2 && (
            <button
              className="js-forgot-password__link forgot-password link-button"
              data-variant="button-as-link"
              type="button"
              onClick={handleForgotPasswordClick}
            >
              <Tmnt tmnt={forgotPassword} isHtml />
            </button>
          )}

          <Button
            classList="btn-signin first recognizeCustomerForm-continue-button"
            isDisabled={submitButtonDisabledStyle}
            leftIcon={submitButtonLeftIcon}
            isProcessing={submitButtonLeftIcon === 'spinner'}
            variant="secondary"
            type="submit"
            isFullWidth
          >
            {submitButtonLeftIcon === 'check' && (
            <span className="font-icon">
              <Icon icon="check" />
            </span>
            )}
            <span>
              {step === 1
                ? (submitButtonLabel || continueButton?.value)
                : (submitButtonLabel || signInButton?.value)}
            </span>
          </Button>

          {step === 2 && (
            <Button
              classList="tertiary-button btn-continue-guest"
              variant=""
              type="button"
              isFullWidth
              onClick={continueAsGuest}
            >
              <Tmnt tmnt={continueAsGuestButton} isHtml />
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}

RecognizeCustomerForm.defaultProps = {
  tmntData: {},
  isEmailSaved: '',
};

RecognizeCustomerForm.propTypes = {
  unmount: PropTypes.func.isRequired,
  tmntData: PropTypes.instanceOf(Object),
  isEmailSaved: PropTypes.string,
};
